import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { Asset, Entry } from 'contentful';

/**
 * Returns the contentful data for HSA/FSA.
 */
export const useFlexContent = (content: FlexContent | undefined) => {
  const { formatMessage } = useIntl();

  return useMemo(() => {
    const title = content?.title ?? formatMessage(fallbacks.title);
    const eligibilityCta = content?.eligibilityCta ?? formatMessage(fallbacks.eligibilityCta);
    const coverageCta = content?.coverageCta ?? formatMessage(fallbacks.coverageCta);
    const eligibleCta = content?.eligibleCta ?? formatMessage(fallbacks.eligibleCta);
    const switchPaymentMethodCta = content?.switchPaymentMethodCta ?? formatMessage(fallbacks.switchPaymentMethodCta); // prettier-ignore
    const disclaimerLabel = content?.disclaimerLabel ?? formatMessage(fallbacks.disclaimerLabel);
    const disclaimerDescription = content?.disclaimerDescription ?? formatMessage(fallbacks.disclaimerDescription); // prettier-ignore
    const heroImageXsUrl = content?.heroImageXs?.fields?.file?.url;
    const heroImageXsA11y = content?.heroImageXs?.fields?.description;
    const heroImageSmUrl = content?.heroImageSm?.fields?.file?.url;
    const heroImageSmA11y = content?.heroImageSm?.fields?.description;
    const guideTitle = content?.guideTitle ?? formatMessage(fallbacks.guideTitle);
    const guideEntries = mapGuideEntries(content?.guideEntries);
    const banner = content?.banner ?? formatMessage(fallbacks.banner);
    const coverageHint = content?.coverageHint ?? formatMessage(fallbacks.coverageHint);
    const coverageTitle = content?.coverageTitle ?? formatMessage(fallbacks.coverageTitle);
    const coverageDescription = content?.coverageDescription ?? formatMessage(fallbacks.coverageDescription); // prettier-ignore
    const coverageImageUrl = content?.coverageImage?.fields?.file?.url;
    const coverageImageA11y = content?.coverageImage?.fields?.description;
    const faqTitle = content?.faqTitle ?? formatMessage(fallbacks.faqTitle);
    const faqDescription = content?.faqDescription ?? formatMessage(fallbacks.faqDescription);
    const mappedFaqEntries = mapFaqEntries(content?.faqEntries);
    const faqEntries = mappedFaqEntries.length > 0 ? mappedFaqEntries : getFallbackFaqEntries(formatMessage); // prettier-ignore

    return {
      title,
      eligibilityCta,
      coverageCta,
      eligibleCta,
      switchPaymentMethodCta,
      disclaimerLabel,
      disclaimerDescription,
      heroImageXsUrl,
      heroImageXsA11y,
      heroImageSmUrl,
      heroImageSmA11y,
      guideTitle,
      guideEntries,
      banner,
      coverageHint,
      coverageTitle,
      coverageDescription,
      coverageImageUrl,
      coverageImageA11y,
      faqTitle,
      faqDescription,
      faqEntries,
    };
  }, [content, formatMessage]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mapGuideEntries(entries: FlexContent['guideEntries']) {
  return (
    entries?.flatMap((entry) => {
      const title = entry?.fields?.title;
      const description = entry?.fields?.description;
      const imageUrl = entry?.fields?.image?.fields?.file?.url;
      const imageA11y = entry?.fields?.image?.fields?.description || title;

      if (!title || !description || !imageUrl || !imageA11y) return [];

      return [{ id: entry.sys.id, title, description, imageUrl, imageA11y }];
    }) ?? []
  );
}

function mapFaqEntries(entries: FlexContent['faqEntries']) {
  return (
    entries?.flatMap((entry) => {
      const title = entry?.fields?.title;
      const description = entry?.fields?.description;

      if (!title || !description) return [];

      return [{ id: entry.sys.id, title, description }];
    }) ?? []
  );
}

function getFallbackFaqEntries(formatMessage: ReturnType<typeof useIntl>['formatMessage']) {
  return [
    {
      id: 'relationship',
      title: formatMessage(fallbacks.faqRelationshipTitle),
      description: formatMessage(fallbacks.faqRelationshipDescription),
    },
    {
      id: 'payment',
      title: formatMessage(fallbacks.faqPaymentTitle),
      description: formatMessage(fallbacks.faqPaymentDescription),
    },
    {
      id: 'availability',
      title: formatMessage(fallbacks.faqAvailabilityTitle),
      description: formatMessage(fallbacks.faqAvailabilityDescription),
    },
  ];
}

// ─── Fallbacks ───────────────────────────────────────────────────────────────

const fallbacks = defineMessages({
  title: {
    defaultMessage: 'A new way to pay for sweetgreen.',
    description: 'HSA/FSA | Hero | Title',
  },
  eligibilityCta: {
    defaultMessage: 'Check your eligibility',
    description: 'HSA/FSA | Hero | Eligibility CTA',
  },
  coverageCta: {
    defaultMessage: 'Get started',
    description: 'HSA/FSA | Coverage | CTA',
  },
  eligibleCta: {
    defaultMessage: 'Start order',
    description: 'HSA/FSA | Eligible | CTA',
  },
  switchPaymentMethodCta: {
    defaultMessage: 'Update your HSA/FSA card',
    description: 'HSA/FSA | Switch payment method | CTA',
  },
  disclaimerLabel: {
    defaultMessage: 'WE’VE PARTNERED WITH FLEX',
    description: 'HSA/FSA | Disclaimer | Label',
  },
  disclaimerDescription: {
    defaultMessage:
      'Your health benefits can now go a little further. Through Flex, qualifying customers can use HSA/FSA funds toward eligible sweetgreen orders. Health savings, one order at a time.',
    description: 'HSA/FSA | Disclaimer | Description',
  },
  guideTitle: {
    defaultMessage: 'How to Maximize Your Pre-Tax Dollars',
    description: 'HSA/FSA | Guide | Title',
  },
  banner: {
    defaultMessage:
      'We believe what you eat matters. That’s why we use fresh ingredients, cook from scratch with olive and avocado oil, and skip artificial flavors, colors, and dyes. Because investing in your health should start with what’s on your plate.',
    description: 'HSA/FSA | Banner',
  },
  coverageHint: {
    defaultMessage: 'NOW PRE-TAX',
    description: 'HSA/FSA | Coverage | Hint',
  },
  coverageTitle: {
    defaultMessage: 'Put your HSA/FSA to work',
    description: 'HSA/FSA | Coverage | Title',
  },
  coverageDescription: {
    defaultMessage:
      'Sweetgreen and Flex give qualifying customers another way to use their HSA/FSA funds, so pre-tax dollars can go toward food that fuels.',
    description: 'HSA/FSA | Coverage | Description',
  },
  faqTitle: {
    defaultMessage: 'Have questions?',
    description: 'HSA/FSA | FAQ | Title',
  },
  faqDescription: {
    defaultMessage:
      'Everything you need to know about ordering and using your HSA/FSA card with Flex.',
    description: 'HSA/FSA | FAQ | Description',
  },
  faqRelationshipTitle: {
    defaultMessage: 'What is Flex, and how does it work with sweetgreen?',
    description: 'HSA/FSA | FAQ | Flex relationship | Title',
  },
  faqRelationshipDescription: {
    defaultMessage:
      'Flex helps qualifying customers use HSA/FSA funds toward eligible purchases. We work with Flex to make the eligibility and payment process as seamless as possible.',
    description: 'HSA/FSA | FAQ | Flex relationship | Description',
  },
  faqPaymentTitle: {
    defaultMessage: 'What is an HSA or FSA?',
    description: 'HSA/FSA | FAQ | Payment | Title',
  },
  faqPaymentDescription: {
    defaultMessage:
      'HSAs and FSAs let you set aside pre-tax dollars for qualified health expenses. HSAs are generally tied to high-deductible health plans and funds can roll over year to year, while FSAs are typically employer-sponsored and may have different rules around when funds need to be used.',
    description: 'HSA/FSA | FAQ | Payment | Description',
  },
  faqAvailabilityTitle: {
    defaultMessage: 'Can I use my HSA/FSA at sweetgreen?',
    description: 'HSA/FSA | FAQ | Card availability | Title',
  },
  faqAvailabilityDescription: {
    defaultMessage:
      'Qualifying customers can use HSA/FSA funds toward eligible sweetgreen orders. You’ll first complete a brief eligibility consultation through Flex to determine whether you qualify.',
    description: 'HSA/FSA | FAQ | Card availability | Description',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GuideEntry = Entry<{
  contentfulEntryTitle?: string;
  title?: string;
  description?: string;
  image?: Asset;
}>;

type FaqEntry = Entry<{
  contentfulEntryTitle?: string;
  title?: string;
  description?: string;
}>;

export type FlexContent = {
  title?: string;
  eligibilityCta?: string;
  coverageCta?: string;
  eligibleCta?: string;
  switchPaymentMethodCta?: string;
  disclaimerLabel?: string;
  disclaimerDescription?: string;
  heroImageXs?: Asset;
  heroImageSm?: Asset;
  guideTitle?: string;
  guideEntries?: GuideEntry[];
  banner?: string;
  coverageHint?: string;
  coverageTitle?: string;
  coverageDescription?: string;
  coverageImage?: Asset;
  faqTitle?: string;
  faqDescription?: string;
  faqEntries?: FaqEntry[];
};
